import type { CollectionAccess, CollectionRole, Member, Root, TaskAccess, TaskRole } from "../../domain/app-types.ts";
import type { Category } from "../../domain/entities.ts";

export function roleMapKey<Role extends string>(roles: Map<string, Role>): string {
  return [...roles].map(([id, role]) => `${id}:${role}`).sort().join(",");
}

export function idSetKey(ids: Set<string>): string { return [...ids].sort().join(","); }
export function rootAccessKey(roots: Root[]): string { return roots.map(root => `${root.id}:${root.role}`).sort().join(","); }

export interface AccessRefreshCapabilities {
  request(path: string, init?: RequestInit): Promise<Response>;
  roots(): Root[];
  setRoots(roots: Root[]): void;
  taskAccessRoles(): Map<string, TaskRole>;
  setTaskAccess(access: TaskAccess[]): void;
  collectionAccessRoles(): Map<string, CollectionRole>;
  setCollectionAccess(access: CollectionAccess[]): void;
  outgoingSharedTaskIds(): Set<string>;
  setOutgoingSharedTaskIds(ids: Set<string>): void;
  taskOnlyRootIds(): Set<string>;
  setTaskOnlyRootIds(ids: Set<string>): void;
  collectionRootIds(): Set<string>;
  setCollectionRootIds(ids: Set<string>): void;
  activeRoot(): string | null;
  setActiveRoot(id: string | null): void;
  clearCategory(): void;
  clearSelectedTask(): void;
  categories(): Category[];
  setDirectory(members: Member[]): void;
  persist(key: string, value: unknown): Promise<void>;
  accessScopeKey(): string;
  render(): Promise<void>;
  sync(): void;
}

export function createAccessRefreshController(capabilities: AccessRefreshCapabilities) {
  const refreshDirectory = async (): Promise<void> => {
    const accessible = capabilities.roots().filter(root => !capabilities.taskOnlyRootIds().has(root.id) && !capabilities.collectionRootIds().has(root.id));
    const results = await Promise.all(accessible.map(async root => {
      const response = await capabilities.request(`/api/v1/roots/${root.id}/assignees`, { credentials: "include" }).catch(() => null);
      return response?.ok ? (await response.json() as Member[]).map(member => ({ ...member, rootId: root.id })) : [];
    }));
    const collectionResults = await Promise.all(capabilities.categories().map(async category => {
      const response = await capabilities.request(`/api/v1/collections/${category.id}/assignees`, { credentials: "include" }).catch(() => null);
      return response?.ok ? (await response.json() as Member[]).map(member => ({ ...member, rootId: category.rootId, collectionId: category.id })) : [];
    }));
    capabilities.setDirectory([...results.flat(), ...collectionResults.flat()]);
  };

  const refreshAccess = async (): Promise<void> => {
    const previousAccessKey = roleMapKey(capabilities.taskAccessRoles());
    const previousCollectionAccessKey = roleMapKey(capabilities.collectionAccessRoles());
    const previousTaskOnlyKey = idSetKey(capabilities.taskOnlyRootIds());
    const previousOutgoingKey = idSetKey(capabilities.outgoingSharedTaskIds());
    const [response, accessResponse, collectionAccessResponse, sharedResponse] = await Promise.all([
      capabilities.request("/api/v1/roots", { credentials: "include" }).catch(() => null),
      capabilities.request("/api/v1/task-access", { credentials: "include" }).catch(() => null),
      capabilities.request("/api/v1/collection-access", { credentials: "include" }).catch(() => null),
      capabilities.request("/api/v1/shared-tasks", { credentials: "include" }).catch(() => null),
    ]);
    if (!response?.ok) { capabilities.sync(); return; }
    if (accessResponse?.ok) capabilities.setTaskAccess(await accessResponse.json() as TaskAccess[]);
    if (collectionAccessResponse?.ok) capabilities.setCollectionAccess(await collectionAccessResponse.json() as CollectionAccess[]);
    if (sharedResponse?.ok) capabilities.setOutgoingSharedTaskIds(new Set(await sharedResponse.json() as string[]));
    const rawRoots = await response.json() as Root[];
    const nextTaskOnlyRootIds = new Set(rawRoots.filter(root => root.role === "task_viewer").map(root => root.id));
    const nextCollectionRootIds = new Set(rawRoots.filter(root => root.role === "collection_viewer").map(root => root.id));
    const nextRoots = rawRoots.map(root => root.role === "task_viewer" || root.role === "collection_viewer" ? { ...root, role: "viewer" as const } : root);
    const accessChanged = rootAccessKey(capabilities.roots()) !== rootAccessKey(nextRoots)
      || previousAccessKey !== roleMapKey(capabilities.taskAccessRoles())
      || previousCollectionAccessKey !== roleMapKey(capabilities.collectionAccessRoles())
      || previousTaskOnlyKey !== idSetKey(nextTaskOnlyRootIds)
      || previousOutgoingKey !== idSetKey(capabilities.outgoingSharedTaskIds());
    if (accessChanged) {
      await capabilities.persist("cursor", 0); capabilities.setRoots(nextRoots); capabilities.setTaskOnlyRootIds(nextTaskOnlyRootIds); capabilities.setCollectionRootIds(nextCollectionRootIds); await capabilities.persist("knownRoots", capabilities.accessScopeKey());
      if (!nextRoots.some(root => root.id === capabilities.activeRoot())) { const next = nextRoots[0]?.id ?? null; capabilities.setActiveRoot(next); capabilities.clearCategory(); await capabilities.persist("activeRoot", next); }
      capabilities.clearSelectedTask(); await capabilities.render();
    }
    await refreshDirectory(); capabilities.sync();
  };

  return { refreshAccess, refreshDirectory };
}
